import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { ClockIcon, DeleteIcon, PenIcon, QuestionMarkIcon } from '@repo/ui/icons'
import RoundedIcon from '@repo/ui/rounded-icon'
import Typography from '@repo/ui/typography'
import React from 'react'

import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TransportMeans } from '../../../lib/types/models/transport-means/transport-means'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import InformationToolTip from '../../elements/information-tooltip'

type Props = {
  title: string
  transportMeans: TransportMeans
  className?: string
  tripStep: TripStepTypes.Full
  onEdit: () => void
  onDelete: () => void
}

const TripStepCard: React.FC<Props> = ({
  transportMeans,
  title,
  className,
  tripStep,
  onEdit,
  onDelete
}) => {
  const atLeastOneTimeProvided =
    tripStep.timeBeforeDepartureForLEC ||
    tripStep.timeBeforeDepartureForStaff ||
    tripStep.departureTimeTripProviders.length > 0 ||
    tripStep.timeBeforeDepartureForParents ||
    tripStep.timeBeforeDepartureForGroup ||
    tripStep.timeBeforeArrivalForLEC ||
    tripStep.timeBeforeArrivalForParents ||
    tripStep.foreignProviderAppointmentTime

  return (
    <div
      className={cn(
        'border border-grey-light p-4 flex flex-col gap-4 rounded-xl bg-white min-w-60',
        className
      )}
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {title}
        </Typography>
        <div className="flex gap-2">
          <ActionButton variant={'grey'} icon={PenIcon} onClick={onEdit} />
          <ActionButton variant={'danger'} icon={DeleteIcon} onClick={onDelete} />
        </div>
      </div>

      {/* Flight */}
      <div className="bg-white-hover p-5 flex justify-between items-center rounded-lg">
        <div className="flex flex-col gap-1 items-center w-1/3">
          <Typography variant="body-medium" className="text-center">
            {tripStep.departurePlace.label}
          </Typography>
          <Typography
            variant="body-medium"
            className="text-center"
            color={tailwindColors.grey.lighter}
          >
            {DateFormatter.getFormattedTime(tripStep.departureTime)}
            <small> ({tripStep.departureTimeZone}) </small>
          </Typography>
          <Typography variant="label" color={tailwindColors.grey.light.active}>
            {DateFormatter.DDMMYYYY(tripStep.departureDate)}
          </Typography>
        </div>

        <div className="flex items-center justify-center w-1/3">
          {transportMeans?.picto && (
            <img src={transportMeans?.picto?.path} className="w-12 h-12" />
          )}
        </div>
        <div className="flex flex-col items-center gap-1 w-1/3">
          {tripStep.arrivalPlace ? (
            <>
              <Typography variant="body-medium" className="text-center">
                {tripStep.arrivalPlace?.label}
              </Typography>
              <Typography
                variant="body-medium"
                className="text-center"
                color={tailwindColors.grey.lighter}
              >
                {DateFormatter.getFormattedTime(tripStep.arrivalTime)}
                <small> ({tripStep.arrivalTimeZone}) </small>
              </Typography>
              <Typography variant="label" color={tailwindColors.grey.light.active}>
                {DateFormatter.DDMMYYYY(tripStep.arrivalDate)}
              </Typography>
            </>
          ) : (
            '-'
          )}
        </div>
      </div>

      {/* Equipments */}
      <div className="flex flex-col gap-2">
        {tripStep.transportMeanNumber && (
          <div className="flex justify-between items-center">
            <Typography variant="secondary-body">N° de vol/train</Typography>
            <Badge variant="grey" size="small" text={tripStep.transportMeanNumber} />
          </div>
        )}
        {tripStep.carNumber && (
          <div className="flex justify-between items-center">
            <Typography variant="secondary-body">Numéro de voiture</Typography>
            <Badge variant="grey" size="small" text={tripStep.carNumber} />
          </div>
        )}
      </div>

      {/* Hours */}
      <div className="flex flex-col gap-2">
        {atLeastOneTimeProvided && (
          <div className="flex gap-2 items-center">
            <RoundedIcon variant={'warning'} icon={ClockIcon} size={28} className="rounded" />
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              Heures de convocation | Timezone
            </Typography>
          </div>
        )}
        <div className="flex flex-col gap-1">
          {tripStep.timeBeforeDepartureForLEC && (
            <Badge
              variant="primary"
              size="small"
              text={
                <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                  LEC -{' '}
                  <b>
                    {DateFormatter.getFormattedTime(tripStep.timeBeforeDepartureForLEC)} (
                    {tripStep.departureTimeZone})
                  </b>
                </Typography>
              }
            />
          )}

          {tripStep.timeBeforeDepartureForStaff && (
            <Badge
              variant="primary"
              size="small"
              text={
                <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                  Encadrant -{' '}
                  <b>
                    {DateFormatter.getFormattedTime(tripStep.timeBeforeDepartureForStaff)} (
                    {tripStep.departureTimeZone})
                  </b>
                </Typography>
              }
            />
          )}
          {tripStep.timeBeforeDepartureForGroup && (
            <Badge
              variant="primary"
              size="small"
              text={
                <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                  Groupe -{' '}
                  <b>
                    {DateFormatter.getFormattedTime(tripStep.timeBeforeDepartureForGroup)} (
                    {tripStep.departureTimeZone})
                  </b>
                </Typography>
              }
            />
          )}
          {tripStep.departureTimeTripProviders.length > 0 && (
            <div
              className="flex flex-wrap gap-2 overflow-x-auto overflow-y-hidden scroll-smooth"
              style={{
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none', // For Firefox
                msOverflowStyle: 'none' // For IE and Edge
              }}
            >
              {tripStep.departureTimeTripProviders?.map((departureTimeTripProvider, index) => (
                <Badge
                  key={index} // Ensure a unique key for each item
                  variant="primary"
                  size="small"
                  className="h-fit"
                  text={
                    <Typography
                      variant="label-medium"
                      color={tailwindColors.primary.DEFAULT}
                      className="py-1"
                    >
                      {departureTimeTripProvider.tripProvider.companyName}
                      {departureTimeTripProvider.meetingPlace
                        ? ` (RDV : ${departureTimeTripProvider.meetingPlace})`
                        : ''}{' '}
                      - <b>{DateFormatter.getFormattedTime(departureTimeTripProvider.time)}</b>
                    </Typography>
                  }
                />
              ))}
            </div>
          )}

          {tripStep.timeBeforeDepartureForParents && (
            <Badge
              variant="primary"
              size="small"
              text={
                <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                  Parent -{' '}
                  <b>
                    {DateFormatter.getFormattedTime(tripStep.timeBeforeDepartureForParents)} (
                    {tripStep.departureTimeZone})
                  </b>
                </Typography>
              }
            />
          )}
          {tripStep.timeBeforeArrivalForLEC && (
            <Badge
              variant="primary"
              size="small"
              text={
                <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                  LEC -{' '}
                  <b>
                    {DateFormatter.getFormattedTime(tripStep.timeBeforeArrivalForLEC)} (
                    {tripStep.arrivalTimeZone})
                  </b>
                </Typography>
              }
            />
          )}
          {tripStep.timeBeforeArrivalForParents && (
            <Badge
              variant="primary"
              size="small"
              text={
                <Typography variant="label-medium" color={tailwindColors.primary.DEFAULT}>
                  Parent -{' '}
                  <b>
                    {DateFormatter.getFormattedTime(tripStep.timeBeforeArrivalForParents)} (
                    {tripStep.arrivalTimeZone})
                  </b>
                </Typography>
              }
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {tripStep.olGuidelineContent && (
          <div className="flex gap-2 items-center">
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              Consignes à la compagnie
            </Typography>
            <InformationToolTip
              align="center"
              icon={
                <QuestionMarkIcon
                  width="22px"
                  height="22px"
                  color={tailwindColors.secondary.light.DEFAULT}
                  color2={tailwindColors.secondary.DEFAULT}
                />
              }
            >
              <Typography
                variant="label-medium"
                color={tailwindColors.white.DEFAULT}
                className="max-w-80"
              >
                {tripStep.olGuidelineContent}
              </Typography>
            </InformationToolTip>
          </div>
        )}
        {tripStep.tripProviderGuidelineContent && (
          <div className="flex gap-2 items-center">
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              {tripStep.directionality === TripStepTypeEnum.INBOUND
                ? "Consignes à l'arrivée pour le prestataire à l'étranger"
                : "Consignes au départ pour le prestataire à l'étranger"}
            </Typography>
            <InformationToolTip
              align="center"
              icon={
                <QuestionMarkIcon
                  width="22px"
                  height="22px"
                  color={tailwindColors.secondary.light.DEFAULT}
                  color2={tailwindColors.secondary.DEFAULT}
                />
              }
            >
              <Typography
                variant="label-medium"
                color={tailwindColors.white.DEFAULT}
                className="max-w-80"
              >
                {tripStep.tripProviderGuidelineContent}
              </Typography>
            </InformationToolTip>
          </div>
        )}
      </div>
    </div>
  )
}

export default TripStepCard
